import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { getExternalLinksForResource, reinstateExternalLink, type ExternalLinkListItem } from "./externalLinks.api";
import {
  copyToClipboard,
  formatExpiryForInternal,
  formatShortDate,
  LINK_DIALOG_WIDTH,
  linkStatusLabel,
  permissionLabel,
  revokedByLabel,
  revokedOnLabel,
} from "./externalLinks.helpers";
import type { ExternalLinkTarget } from "./CreateExternalLinkModal";

/**
 * "Manage shared links" — every link on one file or folder, with Edit / View Stats / Copy URL /
 * Revoke.
 *
 * Reached from the context menu, and separate from the row's link badge on purpose: the badge only
 * says that links exist, this is where they are acted on. Lists links created by ANY user, since
 * any full-access user on the item can manage them — not just the original creator.
 */
type ManageExternalLinksModalProps = {
  target: ExternalLinkTarget;
  onClose: () => void;
  onCreate: () => void;
  onEdit: (link: ExternalLinkListItem) => void;
  onStats: (link: ExternalLinkListItem) => void;
  onRevoke: (link: ExternalLinkListItem) => void;
  /**
   * Asks the creator for a fresh expiry before reinstating a link whose stored expiry has already
   * passed (PRD 3.4). Routed through Edit rather than a bespoke date prompt: the same validation, the
   * same picker, and the link becomes active as part of saving.
   */
  onReinstateNeedsExpiry: (link: ExternalLinkListItem) => void;
  /** Bumped by the parent after a create/edit/revoke so the list refetches. */
  refreshToken?: number;
  /** Called after a successful reinstate so the parent can refresh badges and counts. */
  onMutated: () => void;
};

function ManageExternalLinksModal({
  target,
  onClose,
  onCreate,
  onEdit,
  onStats,
  onRevoke,
  onReinstateNeedsExpiry,
  onMutated,
  refreshToken = 0,
}: Readonly<ManageExternalLinksModalProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [links, setLinks] = useState<ExternalLinkListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"active" | "revoked">("active");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [reinstating, setReinstating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Always fetches BOTH sets: the tab counts have to be right on first paint, and an item's link
    // list is small enough that a second round-trip per tab switch would cost more than it saves.
    const res = await getExternalLinksForResource({
      resourceId: target.resourceId,
      spaceId: target.spaceId,
      includeRevoked: true,
    });
    if (res.status) setLinks(res.links ?? []);
    else dispatch(showToastNotification({ content: untranslated(res.errorMessage), type: TOAST_TYPE_ERROR }));
    setLoading(false);
  }, [target.resourceId, target.spaceId, dispatch]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const activeLinks = useMemo(() => links.filter((l) => l.isActive), [links]);
  const revokedLinks = useMemo(() => links.filter((l) => !l.isActive), [links]);
  const rows = tab === "active" ? activeLinks : revokedLinks;
  const selected = useMemo(() => rows.find((l) => l._id === selectedId) ?? null, [rows, selectedId]);

  const handleCopy = async (link: ExternalLinkListItem) => {
    const ok = await copyToClipboard(link.url);
    dispatch(
      showToastNotification({
        content: ok ? t("Link copied to clipboard") : t("Could not copy the link"),
        type: ok ? TOAST_TYPE_SUCCESS : TOAST_TYPE_ERROR,
      }),
    );
  };

  /**
   * "Enable Link" — returns a revoked link to Active with the SAME URL, so recipients who still hold
   * the original link start working again.
   *
   * `expiryRequired` comes back on a SUCCESSFUL response, not an error: the server refuses to
   * reinstate a link whose expiry has already passed because it would be dead on arrival. Handling it
   * as a branch here is why the button opens the Edit form instead of silently doing nothing.
   */
  const handleReinstate = async (link: ExternalLinkListItem) => {
    if (reinstating) return;
    setReinstating(true);
    try {
      const res = await reinstateExternalLink({ linkId: link._id });
      if (!res.status) {
        dispatch(showToastNotification({ content: untranslated(res.errorMessage), type: TOAST_TYPE_ERROR }));
        return;
      }
      if (res.expiryRequired) {
        dispatch(
          showToastNotification({
            content: t("This link's expiry date has passed. Set a new one to enable it."),
            type: TOAST_TYPE_WARNING,
          }),
        );
        onReinstateNeedsExpiry(link);
        return;
      }
      dispatch(showToastNotification({ content: t("Link enabled"), type: TOAST_TYPE_SUCCESS }));
      // Back to the tab the link now lives on, with the selection cleared — it is no longer on the
      // Revoked list the toolbar was acting against.
      setTab("active");
      setSelectedId(null);
      onMutated();
    } finally {
      setReinstating(false);
    }
  };

  const tabButton = (key: "active" | "revoked", label: string, count: number) => (
    <button
      type="button"
      onClick={() => {
        setTab(key);
        // Selection is per-tab: keeping a row selected across a switch would leave the toolbar
        // acting on a link that is no longer on screen.
        setSelectedId(null);
      }}
      className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-semibold ${
        tab === key ? "border-system_colour_1 text-primary_text" : "border-transparent text-text_light"
      }`}
    >
      <span>{label}</span>
      <span className="rounded-full bg-light_gray px-2 py-0.5 text-xs">{count}</span>
    </button>
  );

  /** Toolbar actions all act on the single selected row; Revoke is only offered on the Active tab. */
  const toolbarButton = (label: string, icon: string, onClick: () => void, disabled: boolean) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium ${
        disabled ? "cursor-not-allowed text-disabled_text_gray" : "text-primary_text hover:bg-card_hover_color"
      }`}
    >
      <IconComponent name={icon} width="1rem" height="1rem" />
      <span>{label}</span>
    </button>
  );

  return (
    <ModalStateDialogComponent
      isVisible
      width={LINK_DIALOG_WIDTH}
      headerTitle={
        <div className="flex min-w-0 items-center gap-2">
          <Text className="truncate">{t("Manage shared links")}</Text>
          <Text className="truncate text-sm font-normal text-text_light">{untranslated(`— ${target.name}`)}</Text>
        </div>
      }
      onCancel={onClose}
      modelContent={
        <div className="flex flex-col">
          <div className="flex items-center gap-6 border-b border-stroke_primary">
            {tabButton("active", t("Active"), activeLinks.length)}
            {tabButton("revoked", t("Revoked"), revokedLinks.length)}
          </div>

          <div className="flex items-center gap-1 border-b border-stroke_primary py-2">
            {/* Active: Edit / View Stats / Copy URL / Revoke. Revoked: Enable Link / View Stats.
                Editing or copying a dead link is meaningless, so those are absent rather than
                disabled — unlike the selection-dependent state, which is disabled, not hidden. */}
            {tab === "active" ? (
              <>
                {/* PRD 3.4: Create link is the action bar's tinted primary; Copy lives on the row
                    (hover), matching the details panel and the prototype. */}
                <button
                  type="button"
                  onClick={onCreate}
                  className="flex items-center gap-2 rounded-md border border-system_colour_1 bg-selection_primary px-3 py-1.5 text-sm font-semibold"
                  data-testid="createExternalLinkFromManage"
                >
                  <IconComponent name="add" width="0.875rem" height="0.875rem" />
                  <span>{t("Create link")}</span>
                </button>
                {toolbarButton(t("Edit"), "editProfile", () => selected && onEdit(selected), !selected)}
                {toolbarButton(t("View Stats"), "activity", () => selected && onStats(selected), !selected)}
                {toolbarButton(t("Revoke"), "delete", () => selected && onRevoke(selected), !selected)}
              </>
            ) : (
              <>
                {toolbarButton(
                  t("Enable Link"),
                  "refresh",
                  () => selected && handleReinstate(selected),
                  !selected || reinstating,
                )}
                {toolbarButton(t("View Stats"), "activity", () => selected && onStats(selected), !selected)}
              </>
            )}
            <div className="flex-1" />
            <Text className="pe-2 text-xs text-text_light">
              {selected
                ? t("{{count}} selected", { count: 1 })
                : tab === "active"
                  ? t("Select a link to edit, view stats or revoke")
                  : t("Select a revoked link to enable it again or view its stats")}
            </Text>
          </div>

          <div className="max-h-[24rem] min-h-[12rem] overflow-auto">
            <div className="grid grid-cols-[2.5rem_minmax(0,1.6fr)_8rem_8rem_7rem_9rem_9rem] items-center border-b border-stroke_primary bg-light_background px-3 py-2 text-xs font-semibold text-text_light">
              <span />
              <span>{t("Link Name")}</span>
              <span>{t("Permission")}</span>
              <span>{t("Expiry")}</span>
              <span>{tab === "active" ? t("Password") : t("Status")}</span>
              <span>{tab === "active" ? t("Created On") : t("Revoked On")}</span>
              <span>{tab === "active" ? t("Created By") : t("Revoked By")}</span>
            </div>

            {loading && <div className="px-3 py-10 text-center text-sm text-text_light">{t("Loading...")}</div>}
            {!loading && !rows.length && (
              <div className="px-3 py-10 text-center text-sm text-text_light">
                {tab === "active" ? t("No active links on this item") : t("No revoked links on this item")}
              </div>
            )}

            {!loading &&
              rows.map((link) => (
                <div
                  key={link._id}
                  onClick={() => setSelectedId(link._id)}
                  onDoubleClick={() => onEdit(link)}
                  onMouseEnter={() => setHoveredId(link._id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`grid cursor-pointer grid-cols-[2.5rem_minmax(0,1.6fr)_8rem_8rem_7rem_9rem_9rem] items-center border-b border-stroke_primary px-3 py-2 text-sm ${
                    selectedId === link._id ? "bg-row_selection_color" : "hover:bg-card_hover_color"
                  }`}
                >
                  {/* Single-select semantics behind a checkbox, per the prototype: checking a row IS
                      selecting it, and only one row can be acted on at a time. */}
                  <span className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedId === link._id}
                      onChange={() => setSelectedId(selectedId === link._id ? null : link._id)}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={link.name}
                    />
                  </span>
                  <span className="flex min-w-0 items-center gap-2">
                    <IconComponent name="Link" width="0.875rem" height="0.875rem" />
                    <span className="truncate font-medium">{untranslated(link.name)}</span>
                    {hoveredId === link._id && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCopy(link);
                        }}
                        aria-label={t("Copy URL")}
                        title={t("Copy URL")}
                        className="flex-none"
                      >
                        <IconComponent name="copy" width="0.875rem" height="0.875rem" />
                      </button>
                    )}
                  </span>
                  <span>{permissionLabel(t, link.permission)}</span>
                  <span className="text-text_light">{formatExpiryForInternal(t, link.expiresAt)}</span>
                  <span className="text-text_light">
                    {tab === "active" ? (link.hasPassword ? t("Yes") : t("No")) : linkStatusLabel(t, link)}
                  </span>
                  {/* On the Revoked tab these two describe the revocation, not the creation — who
                      killed the link and when is the useful answer there. */}
                  <span className="text-text_light">
                    {untranslated(tab === "active" ? formatShortDate(link.createdAt) : revokedOnLabel(link))}
                  </span>
                  <span className="truncate text-text_light">
                    {untranslated(tab === "active" ? link.createdByName : revokedByLabel(link))}
                  </span>
                </div>
              ))}
          </div>
        </div>
      }
      footerContent={
        <div className="flex w-full items-center gap-3">
          <Text className="text-xs text-text_light">{t("{{count}} links", { count: rows.length })}</Text>
        </div>
      }
    />
  );
}

export default ManageExternalLinksModal;
